import React, { useEffect, useState, useRef, useCallback } from 'react';
import { t } from './i18n';

// Detects when a newer frontend build has been deployed while this tab stayed
// open, and offers a one-tap refresh. This exists because the app is a SPA:
// logging out/in or navigating around does NOT re-download the program code, so
// a user who keeps a tab open (especially on mobile) can run a stale build for
// days and never see fixes — which is exactly what bit an owner who "couldn't
// see messages" until a hard refresh.
//
// No build-time version stamping is needed: CRA fingerprints the main bundle
// filename (static/js/main.<hash>.js), so we just compare the bundle this page
// is currently running against the one a freshly-fetched index.html references.
// If they differ, a new build is live.

const BUNDLE_RE = /static\/js\/main\.[A-Za-z0-9]+\.js/;

function runningBundle() {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const s of scripts) {
    const m = (s.getAttribute('src') || '').match(BUNDLE_RE);
    if (m) return m[0];
  }
  return null; // dev server / non-fingerprinted build → detection disabled
}

export default function UpdateBanner() {
  const [stale, setStale] = useState(false);
  const loaded = useRef(runningBundle());

  const check = useCallback(async () => {
    if (!loaded.current || stale) return;
    try {
      // Bypass the HTTP cache so we see what's actually deployed right now.
      const res = await fetch(`/?_=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const html = await res.text();
      const m = html.match(BUNDLE_RE);
      if (m && m[0] !== loaded.current) setStale(true);
    } catch (_) {
      /* offline or transient — ignore and try again later */
    }
  }, [stale]);

  useEffect(() => {
    if (!loaded.current) return; // nothing to detect in dev
    const interval = setInterval(check, 5 * 60 * 1000); // every 5 minutes
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);
    // First check shortly after load covers "left the tab open overnight".
    const t = setTimeout(check, 15 * 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(t);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  if (!stale) return null;

  return (
    <div
      role="status"
      style={{
        // Sit ABOVE the edit Save bar when it's open (it publishes its height as
        // --jc-savebar-h) so the banner never covers the Save button.
        position: 'fixed', left: 0, right: 0, bottom: 'var(--jc-savebar-h, 0px)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 14, flexWrap: 'wrap',
        padding: '12px 16px',
        background: '#1e3a5f', color: '#fff',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.18)',
        fontSize: 15,
      }}
    >
      <span>{t('A new version of Jewish Compass is available.')}</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#fff', color: '#1e3a5f', border: 'none',
          borderRadius: 6, padding: '8px 18px', fontSize: 15, fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {t('Refresh')}
      </button>
    </div>
  );
}
